import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { SearchOutlined, PlusOutlined, EyeOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';

import { useTypedSelector } from '../../hooks/useTypedSelector';
import { useActions } from '../../hooks/useActions';
import { useResponsive } from '../../hooks/useResponsive';
import { IAnswerSheetsState } from '../../store/reducers/answer_sheets/types';

import { PageTitle } from '../../components/common/PageTitle/PageTitle';
import { PageContainer } from '../../components/layouts/PageLayouts/PageContainer/PageContainer';
import { ContentCard } from '../../components/layouts/PageLayouts/ContentCard/ContentCard';
import { BusinessButton } from '../../components/common/buttons/BusinessButton/BusinessButton';
import { notificationController } from '../../controllers/notificationController';
import { CreateAnswerSheetModal, AnswerSheetFormData } from '../../components/common/modals/CreateAnswerSheetModal';
import { ViewAnswerSheetModal } from '../../components/common/modals/ViewAnswerSheetModal';
import { EditAnswerSheetModal } from '../../components/common/modals/EditAnswerSheetModal';
import { DeleteConfirmModal } from '../../components/common/modals/DeleteConfirmModal';

// Styled Components
const HeaderSection = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
  gap: 20px;
  
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;
    gap: 16px;
  }
`;

const TitleSection = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  
  .title {
    font-size: 24px;
    font-weight: 700;
    color: #2c3e50;
    margin: 0;
    display: flex;
    align-items: center;
    gap: 8px;
    
    &::before {
      content: '📝';
      font-size: 28px;
    }
  }
  
  .subtitle {
    font-size: 14px;
    color: rgba(44, 62, 80, 0.7);
    margin: 4px 0 0 36px;
  }
  
  @media (max-width: 768px) {
    .title {
      font-size: 20px;
      
      &::before {
        font-size: 24px;
      }
    }
    
    .subtitle {
      margin-left: 32px;
      font-size: 13px;
    }
  }
`;

const ActionsSection = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  
  @media (max-width: 768px) {
    flex-direction: column;
    width: 100%;
    gap: 12px;
  }
`;

const SearchInput = styled.div`
  position: relative;
  min-width: 320px;
  
  .search-icon {
    position: absolute;
    left: 12px;
    top: 50%;
    transform: translateY(-50%);
    color: rgba(0, 0, 0, 0.4);
    z-index: 1;
  }
  
  input {
    width: 100%;
    padding: 10px 12px 10px 40px;
    border: 1px solid rgba(0, 0, 0, 0.15);
    border-radius: 8px;
    font-size: 14px;
    background: white;
    transition: all 0.2s ease;
    
    &:focus {
      outline: none;
      border-color: #1677ff;
      box-shadow: 0 0 0 2px rgba(22, 119, 255, 0.1);
    }
    
    &::placeholder {
      color: rgba(0, 0, 0, 0.4);
    }
  }
  
  @media (max-width: 768px) {
    min-width: auto;
    width: 100%;
  }
`;

const CreateButton = styled(BusinessButton)`
  min-width: 180px;
  
  @media (max-width: 768px) {
    width: 100%;
    min-width: auto;
  }
`;

// Answer Sheets Cards Styles (matching Grading Scales design)
const SheetsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
  gap: 24px;
  margin-top: 24px;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 16px;
    margin-top: 16px;
  }
`;

const SheetCard = styled.div`
  background: white;
  border-radius: 16px;
  padding: 24px;
  border: 1px solid rgba(0, 0, 0, 0.08);
  transition: all 0.3s ease;
  cursor: pointer;
  position: relative;
  overflow: hidden;
  
  &:hover {
    transform: translateY(-4px) scale(1.02);
    box-shadow: 0 12px 40px rgba(0, 0, 0, 0.15);
    border-color: rgba(22, 119, 255, 0.2);
    
    .actions {
      opacity: 1;
      transform: translateY(0);
    }
  }
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: linear-gradient(90deg, #4096ff 0%, #1677ff 50%, #0958d9 100%);
    opacity: 0;
    transition: opacity 0.3s ease;
  }
  
  &:hover::before {
    opacity: 1;
  }
  
  /* Mobile & tablet - no hover effects, always show top border */
  @media (max-width: 1024px) {
    padding: 20px;
    
    &:hover {
      transform: none;
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
    }
    
    &::before {
      opacity: 1;
    }
    
    &:active {
      transform: scale(0.98);
    }
  }
`;

const SheetHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 16px;
  
  .sheet-name {
    font-size: 18px;
    font-weight: 700;
    color: #2c3e50;
    margin: 0 0 4px 0;
    line-height: 1.3;
  }
  
  .sheet-type {
    font-size: 13px;
    color: rgba(44, 62, 80, 0.6);
    background: rgba(64, 150, 255, 0.1);
    padding: 4px 8px;
    border-radius: 6px;
    font-weight: 500;
  }
`;

const SheetInfo = styled.div`
  margin-bottom: 16px;
  
  .created-date {
    font-size: 13px;
    color: rgba(44, 62, 80, 0.6);
    margin-bottom: 12px;
    display: flex;
    align-items: center;
    gap: 6px;
    
    &::before {
      content: '📅';
      font-size: 14px;
    }
  }
`;

const SheetDetails = styled.div`
  margin-bottom: 20px;
  
  .details-label {
    font-size: 14px;
    font-weight: 600;
    color: #2c3e50;
    margin-bottom: 8px;
  }
  
  .details-list {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
  }
`;

const DetailBadge = styled.span<{ type?: string }>`
  padding: 6px 12px;
  border-radius: 8px;
  font-size: 12px;
  font-weight: 600;
  color: white;
  background: ${props => {
    if (props.type === 'questions') return '#3B82F6';
    if (props.type === 'answers') return '#10B981';
    if (props.type === 'keys') return '#8B5CF6';
    if (props.type === 'paper') return '#F59E0B';
    return '#6B7280';
  }};
  
  transition: all 0.2s ease;
  
  &:hover {
    transform: scale(1.05);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
  }
`;

const CardActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  opacity: 0;
  transform: translateY(10px);
  transition: all 0.3s ease;
  
  &.actions {
    opacity: 0;
  }
  
  /* Always show on mobile and tablet */
  @media (max-width: 1024px) {
    opacity: 1;
    transform: translateY(0);
    
    &.actions {
      opacity: 1;
    }
  }
`;

const ActionButton = styled.button<{ variant?: 'view' | 'edit' | 'delete' }>`
  padding: 8px 16px;
  border: none;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 6px;
  
  ${props => {
    if (props.variant === 'view') return `
      background: rgba(22, 119, 255, 0.1);
      color: #1677ff;
      
      &:hover {
        background: rgba(22, 119, 255, 0.2);
        transform: translateY(-1px);
      }
    `;
    if (props.variant === 'edit') return `
      background: rgba(139, 92, 246, 0.1);
      color: #8B5CF6;
      
      &:hover {
        background: rgba(139, 92, 246, 0.2);
        transform: translateY(-1px);
      }
    `;
    return `
      background: rgba(239, 68, 68, 0.1);
      color: #EF4444;
      
      &:hover {
        background: rgba(239, 68, 68, 0.2);
        transform: translateY(-1px);
      }
    `;
  }}
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 60px 20px;
  color: rgba(0, 0, 0, 0.45);
  
  .icon {
    font-size: 64px;
    margin-bottom: 16px;
    display: block;
  }
  
  .title {
    font-size: 18px;
    font-weight: 600;
    margin-bottom: 8px;
    color: rgba(0, 0, 0, 0.65);
  }
  
  .description {
    font-size: 14px;
    margin-bottom: 24px;
  }
`;

const LoadingState = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
  gap: 24px;
  margin-top: 24px;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 16px;
  }
`;

const SkeletonCard = styled.div`
  background: white;
  border-radius: 16px;
  padding: 20px;
  border: 1px solid rgba(0, 0, 0, 0.08);
  
  .skeleton-line {
    height: 12px;
    background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
    background-size: 200% 100%;
    animation: loading 1.5s infinite;
    border-radius: 6px;
    margin-bottom: 12px;
    
    &.title {
      height: 18px;
      width: 70%;
    }
    
    &.subtitle {
      height: 14px;
      width: 40%;
    }
    
    &.meta {
      height: 12px;
      width: 60%;
    }
  }
  
  @keyframes loading {
    0% {
      background-position: -200% 0;
    }
    100% {
      background-position: 200% 0;
    }
  }
`;

const AnswerSheetsPage: React.FC = () => {
  const { isDesktop } = useResponsive();
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  
  // Redux state
  const { 
    selectedAnswerSheet, 
    selectedId, 
    answerSheets, 
    loading: sheetsLoading, 
    create: createState, 
    update: updateState,
    delete: deleteState 
  } = useTypedSelector<IAnswerSheetsState>(state => state.answer_sheets);
  const { deleteAnswerSheetRequest, getAnswerSheetsRequest, createAnswerSheetRequest, updateAnswerSheetRequest } = useActions();
  
  // Local state
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [createModalVisible, setCreateModalVisible] = useState<boolean>(false);
  const [viewModal, setViewModal] = useState<{ isOpen: boolean; sheet: any | null }>({ isOpen: false, sheet: null });
  const [editModal, setEditModal] = useState<{ isOpen: boolean; sheet: any | null }>({ isOpen: false, sheet: null });
  const [deleteModal, setDeleteModal] = useState<{ isOpen: boolean; sheetId: number | null; sheetName: string }>({ isOpen: false, sheetId: null, sheetName: '' });

  // Get real answer sheets from Redux store

  // Handlers
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
  };

  const handleCreateSheet = () => {
    setCreateModalVisible(true);
  };

  const handleCreateModalClose = () => {
    setCreateModalVisible(false);
  };

  const handleCreateSubmit = (data: AnswerSheetFormData) => {
    // Transform form data to API model
    const answerSheetModel = {
      name: data.name,
      questionsNumber: data.numberOfQuestions,
      numberOfBubbles: data.numberOfAnswers,
      answerKeys: data.answerKeys,
      colorOfBubbles: data.bubbleColor,
      paperSize: data.paperSize
    };
    
    createAnswerSheetRequest(answerSheetModel);
  };

  const handleViewSheet = (sheet: any) => {
    console.log('[VIEW SHEET] Sheet data:', sheet);
    console.log('[VIEW SHEET] imageBase64 exists:', !!sheet?.imageBase64);
    console.log('[VIEW SHEET] imageBase64 length:', sheet?.imageBase64?.length || 0);
    setViewModal({ isOpen: true, sheet });
  };

  const handleViewModalClose = () => {
    setViewModal({ isOpen: false, sheet: null });
  };

  const handleEditSheet = (sheet: any) => {
    setEditModal({ isOpen: true, sheet });
  };

  const handleEditModalClose = () => {
    setEditModal({ isOpen: false, sheet: null });
  };

  const handleEditSubmit = (id: number, data: AnswerSheetFormData) => {
    console.log('[EDIT SUBMIT] Form data from modal:', data);
    const updateModel = {
      name: data.name,
      questionsNumber: data.numberOfQuestions,
      numberOfBubbles: data.numberOfAnswers,
      answerKeys: data.answerKeys,
      colorOfBubbles: data.bubbleColor,
      paperSize: data.paperSize
    };
    console.log('[EDIT SUBMIT] Update model to send:', updateModel);
    updateAnswerSheetRequest(id, updateModel);
  };

  const handleDeleteSheet = (sheetId: number, sheetName: string) => {
    setDeleteModal({ isOpen: true, sheetId, sheetName });
  };

  const handleDeleteModalClose = () => {
    setDeleteModal({ isOpen: false, sheetId: null, sheetName: '' });
  };

  const handleDeleteConfirm = () => {
    if (deleteModal.sheetId) {
      deleteAnswerSheetRequest(deleteModal.sheetId);
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      day: 'numeric', 
      month: 'short',
      year: 'numeric'
    });
  };

  // Filter sheets based on search query
  const filteredSheets: any[] = (answerSheets || []).filter((sheet: any) =>
    (sheet.name || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
    (sheet.paperSize || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
    formatDate(sheet.createdAt || new Date().toISOString()).toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Effects
  useEffect(() => {
    // Load answer sheets (userId already in localStorage from Keycloak)
    const storedUserId = localStorage.getItem("UserId");
    if (storedUserId && storedUserId !== 'null') {
      // Add userId to URL if not present
      if (!searchParams.get('userId')) {
        setSearchParams({ userId: storedUserId });
      }
      
      getAnswerSheetsRequest();
    }
  }, []);

  // Handle successful creation
  useEffect(() => {
    if (createState.success) {
      setCreateModalVisible(false);
      notificationController.success({
        message: 'Success',
        description: 'Answer sheet created successfully!'
      });
      getAnswerSheetsRequest();
    }
  }, [createState.success]);

  // Handle create error
  useEffect(() => {
    if (createState.error) {
      notificationController.error({
        message: 'Failed to create answer sheet',
        description: createState.error
      });
    }
  }, [createState.error]);

  // Handle successful deletion
  useEffect(() => {
    if (deleteState.success) {
      setDeleteModal({ isOpen: false, sheetId: null, sheetName: '' });
      notificationController.success({
        message: 'Success',
        description: 'Answer sheet deleted successfully!'
      });
      getAnswerSheetsRequest();
    }
  }, [deleteState.success]);

  // Handle delete error
  useEffect(() => {
    if (deleteState.error) {
      notificationController.error({
        message: 'Failed to delete answer sheet',
        description: deleteState.error
      });
    }
  }, [deleteState.error]);

  // Handle successful update
  useEffect(() => {
    if (updateState?.success) {
      setEditModal({ isOpen: false, sheet: null });
      notificationController.success({
        message: 'Success',
        description: 'Answer sheet updated successfully!'
      });
      getAnswerSheetsRequest();
    }
  }, [updateState?.success]);

  // Handle update error
  useEffect(() => {
    if (updateState?.error) {
      notificationController.error({
        message: 'Failed to update answer sheet',
        description: updateState.error
      });
    }
  }, [updateState?.error]);

  return (
    <>
      <PageTitle 
        title="Answer Sheets" 
        description="Create and manage answer sheets for assessments" 
        url={window.location.href} 
        noIndex={true}
      />
      
      <PageContainer>
        {/* Header Section */}
        <HeaderSection>
          <TitleSection>
            <div>
              <h1 className="title">Answer Sheets</h1>
              <p className="subtitle">Create and manage answer sheets for assessments</p>
            </div>
          </TitleSection>
          
          <ActionsSection>
            <SearchInput>
              <SearchOutlined className="search-icon" />
              <input
                type="text"
                placeholder="Search by name, class, or date..."
                value={searchQuery}
                onChange={handleSearchChange}
              />
            </SearchInput>
            
            <CreateButton
              variant="primary"
              businessSize="medium"
              onClick={handleCreateSheet}
              icon={<PlusOutlined />}
            >
              Create New Answer Sheet
            </CreateButton>
          </ActionsSection>
        </HeaderSection>

        {/* Content Section */}
        <ContentCard>
          {sheetsLoading ? (
            <LoadingState>
              {[1, 2, 3, 4, 5, 6].map((item) => (
                <SkeletonCard key={item}>
                  <div className="skeleton-line title"></div>
                  <div className="skeleton-line subtitle"></div>
                  <div className="skeleton-line meta"></div>
                </SkeletonCard>
              ))}
            </LoadingState>
          ) : filteredSheets.length > 0 ? (
            <SheetsGrid>
              {filteredSheets.map((sheet: any) => (
                <SheetCard key={sheet.id}>
                  <SheetHeader>
                    <div>
                      <h3 className="sheet-name">{sheet.name || sheet.examName || 'Untitled'}</h3>
                      <span className="sheet-type">{sheet.paperSize || 'A4'}</span>
                    </div>
                  </SheetHeader>
                  
                  <SheetInfo>
                    <div className="created-date">
                      Created {formatDate(sheet.createdAt || sheet.date)}
                    </div>
                  </SheetInfo>
                  
                  <SheetDetails>
                    <div className="details-label">Settings:</div>
                    <div className="details-list">
                      <DetailBadge type="questions">
                        {sheet.questionsNumber || 100} Questions
                      </DetailBadge>
                      <DetailBadge type="answers">
                        {sheet.numberOfBubbles || 4} Answers
                      </DetailBadge>
                      <DetailBadge type="keys">
                        {sheet.answerKeys || 'a-e'}
                      </DetailBadge>
                    </div>
                  </SheetDetails>
                  
                  <CardActions className="actions">
                    <ActionButton 
                      variant="view" 
                      onClick={(e) => {
                        e.stopPropagation();
                        handleViewSheet(sheet);
                      }}
                    >
                      <EyeOutlined />
                      View
                    </ActionButton>
                    <ActionButton 
                      variant="edit" 
                      onClick={(e) => {
                        e.stopPropagation();
                        handleEditSheet(sheet);
                      }}
                    >
                      <EditOutlined />
                      Edit
                    </ActionButton>
                    <ActionButton 
                      variant="delete" 
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteSheet(sheet.id, sheet.name);
                      }}
                    >
                      <DeleteOutlined />
                      Delete
                    </ActionButton>
                  </CardActions>
                </SheetCard>
              ))}
            </SheetsGrid>
          ) : (
            <EmptyState>
              <span className="icon">📝</span>
              <div className="title">
                {searchQuery ? 'No matching answer sheets found' : 'No answer sheets yet'}
              </div>
              <div className="description">
                {searchQuery 
                  ? `Try adjusting your search for "${searchQuery}"` 
                  : 'Create your first answer sheet to get started with assessments'
                }
              </div>
              {!searchQuery && (
                <BusinessButton
                  variant="primary"
                  businessSize="medium"
                  onClick={handleCreateSheet}
                  icon={<PlusOutlined />}
                >
                  Create Your First Answer Sheet
                </BusinessButton>
              )}
            </EmptyState>
          )}
        </ContentCard>
      </PageContainer>
      
      {/* Create Answer Sheet Modal */}
      <CreateAnswerSheetModal
        isOpen={createModalVisible}
        onClose={handleCreateModalClose}
        onSubmit={handleCreateSubmit}
        isLoading={createState.loading}
      />

      {/* View Answer Sheet Modal */}
      <ViewAnswerSheetModal
        isOpen={viewModal.isOpen}
        onClose={handleViewModalClose}
        answerSheet={viewModal.sheet}
        isLoading={false}
      />

      {/* Edit Answer Sheet Modal */}
      <EditAnswerSheetModal
        isOpen={editModal.isOpen}
        onClose={handleEditModalClose}
        onSubmit={handleEditSubmit}
        answerSheet={editModal.sheet}
        isLoading={updateState?.loading}
      />

      {/* Delete Confirmation Modal */}
      <DeleteConfirmModal
        isOpen={deleteModal.isOpen}
        onClose={handleDeleteModalClose}
        onConfirm={handleDeleteConfirm}
        title="Delete Answer Sheet"
        itemName={deleteModal.sheetName}
        warningText="This action cannot be undone. The answer sheet will be permanently deleted."
        confirmButtonText="Delete"
        isLoading={deleteState.loading}
      />
    </>
  );
};

export default AnswerSheetsPage